import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbActiveModal, NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Subscription } from 'rxjs';
import { environment } from '../../../environments/environment';

interface CourseStatus {
  id: number;
  name: string;
  createdAt: string;
  updatedAt: string;
}

@Component({
  selector: 'app-create-course-modal',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    NgbTooltipModule
  ],
  templateUrl: './create-course-modal.component.html',
  styleUrl: './create-course-modal.component.scss'
})
export class CreateCourseModalComponent implements OnInit, OnDestroy {
  createCourseForm!: FormGroup;
  currentStep = 1;
  totalSteps = 2;
  isSubmitting = false;
  
  // Course status properties
  courseStatusList: CourseStatus[] = [];
  isDataLoaded = false;
  
  // Image upload properties
  imageThumbnailFile: File | null = null;
  imageThumbnailPreview: string | null = null;
  imageDetailFile: File | null = null;
  imageDetailPreview: string | null = null;
  
  private subscription = new Subscription();

  constructor(
    private fb: FormBuilder,
    public activeModal: NgbActiveModal,
    private http: HttpClient,
    private cdr: ChangeDetectorRef
  ) {
    this.initForm();
  }

  ngOnInit(): void {
    this.loadCourseStatusList();
  }

  ngAfterViewInit(): void {
    // Ensure change detection runs after view init
    setTimeout(() => {
      if (this.courseStatusList.length > 0) {
        this.isDataLoaded = true;
      }
    }, 100);
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  /**
   * Initialize the reactive form
   */
  private initForm(): void {
    this.createCourseForm = this.fb.group({
      // Course Information
      courseTopic: ['', Validators.required],
      certificationBySEC: [false],
      cpdHours: [0, [Validators.required, Validators.min(0), Validators.max(500)]],
      trainingFormat: ['', Validators.required],
      startDate: ['', Validators.required],
      endDate: ['', Validators.required],
      closeRegisterDate: [''],
      trainingLocation: [''],
      courseFee: [0, [Validators.min(0)]],
      maxParticipants: [0, [Validators.required, Validators.min(1)]],
      courseStatus: [null, Validators.required],
      remarks: [''],
      // Image uploads
      imageThumbnail: [null],
      imageDetail: [null]
    });
  }

  /**
   * Load course status list from API
   */
  loadCourseStatusList(): void {
    const apiUrl = `${environment.apiUrl}/course-status`;
    
    console.log('🔄 Loading course status from:', apiUrl);
    
    // Use observe: 'response' to handle 304 properly
    this.http.get<CourseStatus[]>(apiUrl, { observe: 'response' }).subscribe({
      next: (response) => {
        console.log('📡 API Response Status:', response.status);
        console.log('📦 Response body:', response.body);
        
        if (response.body && Array.isArray(response.body)) {
          this.courseStatusList = response.body;
          console.log('✅ Course status list loaded:', this.courseStatusList.length, 'items');
          console.log('📋 Status list:', this.courseStatusList);
        } else {
          console.warn('⚠️ Response body is empty or not an array');
          this.courseStatusList = [];
        }
        
        this.isDataLoaded = true;
        this.cdr.detectChanges(); // Manually trigger change detection
      },
      error: (error: HttpErrorResponse) => {
        console.error('❌ Error loading course status:', error);
        console.log('Error status:', error.status);
        console.log('Error message:', error.message);
        
        // Even on error, mark as loaded to show form
        this.isDataLoaded = true;
        this.cdr.detectChanges();
        
        // For 304, try to use cached data if available
        if (error.status === 304) {
          console.log('📦 Using cached data (304 Not Modified)');
          // The browser should use cached data automatically
        } else {
          alert('ไม่สามารถโหลดรายการสถานะคอร์สได้: ' + error.message);
        }
      }
    });
  }

  /**
   * Navigate to next step
   */
  nextStep(): void {
    if (this.currentStep < this.totalSteps) {
      if (this.validateCurrentStep()) {
        this.currentStep++;
      }
    }
  }

  /**
   * Navigate to previous step
   */
  prevStep(): void {
    if (this.currentStep > 1) {
      this.currentStep--;
    }
  }

  /**
   * Go to specific step
   */
  goToStep(step: number): void {
    if (step >= 1 && step <= this.totalSteps) {
      if (step <= this.currentStep) {
        this.currentStep = step;
      }
    }
  }

  /**
   * Validate current step
   */
  private validateCurrentStep(): boolean {
    if (this.currentStep === 1) {
      const requiredFields = [
        'courseByVAT',
        'courseTopic',
        'cpdHours',
        'trainingFormat',
        'startDate',
        'endDate',
        'maxParticipants',
        'courseStatus'
      ];
      
      let isValid = true;
      requiredFields.forEach(fieldName => {
        const control = this.createCourseForm.get(fieldName);
        if (control) {
          control.markAsTouched();
          if (control.invalid) {
            isValid = false;
          }
        }
      });
      
      return isValid;
    }
    
    return true;
  }

  /**
   * Check if field has error
   */
  hasError(fieldName: string, errorType?: string): boolean {
    const control = this.createCourseForm.get(fieldName);
    if (!control) return false;
    
    if (errorType) {
      return control.hasError(errorType) && (control.dirty || control.touched);
    }
    
    return control.invalid && (control.dirty || control.touched);
  }

  /**
   * Get error message for field
   */
  getErrorMessage(fieldName: string): string {
    const control = this.createCourseForm.get(fieldName);
    if (!control || !control.errors) return '';
    
    if (control.errors['required']) return 'กรุณากรอกข้อมูลนี้';
    if (control.errors['min']) return `ค่าต้องไม่น้อยกว่า ${control.errors['min'].min}`;
    if (control.errors['max']) return `ค่าต้องไม่เกิน ${control.errors['max'].max}`;
    
    return 'ข้อมูลไม่ถูกต้อง';
  }


  /**
   * Submit form - Upload image first, then create course with image URL
   */
  onSubmit(): void {
    if (this.createCourseForm.valid && !this.isSubmitting) {
      this.isSubmitting = true;
      
      // Step 1: Upload imageDetail first if it exists
      if (this.imageDetailFile) {
        this.uploadImageDetail().then((imageUrl) => {
          // Step 2: Create course with the image URL
          this.createCourseWithData(imageUrl);
        }).catch((error) => {
          console.error('❌ Error uploading image:', error);
          this.isSubmitting = false;
          alert('ไม่สามารถอัปโหลดรูปภาพได้: ' + (error.message || 'เกิดข้อผิดพลาด'));
        });
      } else {
        // No image to upload, create course without imageDetail
        this.createCourseWithData(null);
      }
    } else {
      // Mark all fields as touched to show validation errors
      Object.keys(this.createCourseForm.controls).forEach(key => {
        this.createCourseForm.get(key)?.markAsTouched();
      });
      alert('กรุณากรอกข้อมูลให้ครบถ้วน');
    }
  }

  /**
   * Upload imageDetail to /upload/image endpoint
   */
  private uploadImageDetail(): Promise<string> {
    return new Promise((resolve, reject) => {
      if (!this.imageDetailFile) {
        reject(new Error('No image file to upload'));
        return;
      }

      const uploadUrl = `${environment.apiUrl}/upload/image`;
      const formData = new FormData();
      
      // Add file with field name 'file' and folder 'course-detail'
      formData.append('file', this.imageDetailFile, this.imageDetailFile.name);
      formData.append('folder', 'course-detail');
      
      console.log('📤 Uploading image to:', uploadUrl);
      console.log('📁 Folder: course-detail');
      
      this.http.post<{ url: string }>(uploadUrl, formData).subscribe({
        next: (response) => {
          console.log('✅ Image uploaded successfully:', response);
          if (response && response.url) {
            resolve(response.url);
          } else {
            reject(new Error('No URL returned from upload'));
          }
        },
        error: (error: HttpErrorResponse) => {
          console.error('❌ Image upload failed:', error);
          reject(error);
        }
      });
    });
  }

  /**
   * Create course with the uploaded image URL
   */
  private createCourseWithData(imageDetailUrl: string | null): void {
    const apiUrl = `${environment.apiUrl}/courses`;
    
    // Ensure duration is at least 0.1
    const duration = parseFloat(this.createCourseForm.value.cpdHours) || 0.1;
    
    // Prepare base course data
    const courseData: any = {
      title: this.createCourseForm.value.courseTopic,
      description: this.createCourseForm.value.remarks || '',
      instructor: 'VAT Instructor',
      duration: duration < 0.1 ? 0.1 : duration,
      category: this.mapCourseStatusToCategory(this.createCourseForm.value.courseStatus),
      level: this.mapTrainingFormatToLevel(this.createCourseForm.value.trainingFormat),
      price: parseFloat(this.createCourseForm.value.courseFee) || 0,
      isActive: this.isStatusActive(this.createCourseForm.value.courseStatus),
      seat: parseInt(this.createCourseForm.value.maxParticipants) || 0,
      certificationBySEC: this.createCourseForm.value.certificationBySEC
    };
    
    // Add imageDetail URL if uploaded
    if (imageDetailUrl) {
      courseData.imageDetail = imageDetailUrl;
    }
    
    // Add optional fields
    if (this.createCourseForm.value.startDate) {
      courseData.startDate = this.createCourseForm.value.startDate;
    }
    if (this.createCourseForm.value.endDate) {
      courseData.endDate = this.createCourseForm.value.endDate;
    }
    if (this.createCourseForm.value.closeRegisterDate) {
      courseData.closeRegisterDate = this.createCourseForm.value.closeRegisterDate;
    }
    if (this.createCourseForm.value.trainingLocation) {
      courseData.trainingLocation = this.createCourseForm.value.trainingLocation;
    }
    if (this.createCourseForm.value.courseStatus) {
      courseData.courseStatusId = parseInt(this.createCourseForm.value.courseStatus);
    }
    
    console.log('🔄 Creating new course via POST API:', apiUrl);
    console.log('📤 Course data:', courseData);
    
    // Create course with JSON (proper data types)
    this.http.post(apiUrl, courseData).subscribe({
      next: (response: any) => {
        console.log('✅ Course created successfully:', response);
        this.isSubmitting = false;
        alert('สร้างคอร์สสำเร็จ');
        this.activeModal.close({
          success: true,
          courseData: response
        });
      },
      error: (error: HttpErrorResponse) => {
        console.error('❌ Error creating course:', error);
        this.isSubmitting = false;
        alert('ไม่สามารถสร้างคอร์สได้: ' + (error.error?.message || error.message || 'เกิดข้อผิดพลาด'));
      }
    });
  }

  /**
   * Map training format to level for API
   */
  private mapTrainingFormatToLevel(format: string): string {
    const formatToLevel: { [key: string]: string } = {
      'onsite': 'beginner',
      'online': 'intermediate',
      'hybrid': 'advanced'
    };
    return formatToLevel[format] || 'intermediate';
  }

  /**
   * Map course status to category for API
   */
  private mapCourseStatusToCategory(statusId: number): string {
    // Map status ID to category (keeping for backward compatibility)
    const statusIdToCategory: { [key: number]: string } = {
      1: 'Accounting',        // เตรียมเปิดคอร์ส
      2: 'Accounting',        // เปิดรับสมัคร
      3: 'Property Valuation', // ปิดรับสมัคร
      4: 'Accounting'         // ยกเลิกคอร์ส
    };
    return statusIdToCategory[statusId] || 'Accounting';
  }

  /**
   * Check if status is active based on status ID
   */
  private isStatusActive(statusId: number): boolean {
    // Status ID 4 = ยกเลิกคอร์ส (cancelled) should be inactive
    return statusId !== 4;
  }

  /**
   * Get step title
   */
  getStepTitle(step: number): string {
    switch (step) {
      case 1: return 'ข้อมูลคอร์ส';
      case 2: return 'ตรวจสอบและยืนยัน';
      default: return '';
    }
  }

  /**
   * Get step icon
   */
  getStepIcon(step: number): string {
    switch (step) {
      case 1: return 'bi-book';
      case 2: return 'bi-check-circle';
      default: return 'bi-circle';
    }
  }

  /**
   * Check if step is completed
   */
  isStepCompleted(step: number): boolean {
    return step < this.currentStep;
  }

  /**
   * Check if step is current
   */
  isStepCurrent(step: number): boolean {
    return step === this.currentStep;
  }

  /**
   * Get progress percentage
   */
  getProgressPercentage(): number {
    return (this.currentStep / this.totalSteps) * 100;
  }

  /**
   * Format date for display
   */
  formatDate(dateString: string): string {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('th-TH', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return dateString;
    }
  }

  /**
   * Get training format label
   */
  getTrainingFormatLabel(format: string): string {
    const formats: { [key: string]: string } = {
      'onsite': 'อบรมในสถานที่',
      'online': 'อบรมออนไลน์',
      'hybrid': 'อบรมแบบผสม (Onsite + Online)'
    };
    return formats[format] || format;
  }

  /**
   * Get course status label by ID
   */
  getCourseStatusLabel(statusId: number): string {
    const status = this.courseStatusList.find(s => s.id === statusId);
    return status ? status.name : '-';
  }

  /**
   * Get course status badge class by ID
   */
  getCourseStatusBadgeClass(statusId: number): string {
    // Map status ID to badge class
    const classes: { [key: number]: string } = {
      1: 'bg-secondary',  // เตรียมเปิดคอร์ส
      2: 'bg-success',    // เปิดรับสมัคร
      3: 'bg-warning',    // ปิดรับสมัคร
      4: 'bg-danger'      // ยกเลิกคอร์ส
    };
    return classes[statusId] || 'bg-secondary';
  }

  /**
   * Handle image thumbnail file selection
   */
  onImageThumbnailSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];
      
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
      if (!allowedTypes.includes(file.type)) {
        alert('กรุณาเลือกไฟล์รูปภาพ (JPG, PNG, GIF) เท่านั้น');
        return;
      }
      
      // Validate file size (max 5MB)
      const maxSize = 5 * 1024 * 1024; // 5MB in bytes
      if (file.size > maxSize) {
        alert('ขนาดไฟล์ต้องไม่เกิน 5MB');
        return;
      }
      
      this.imageThumbnailFile = file;
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (e: ProgressEvent<FileReader>) => {
        this.imageThumbnailPreview = e.target?.result as string;
      };
      reader.readAsDataURL(file);
    }
  }

  /**
   * Handle image detail file selection
   */
  onImageDetailSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];
      
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
      if (!allowedTypes.includes(file.type)) {
        alert('กรุณาเลือกไฟล์รูปภาพ (JPG, PNG, GIF) เท่านั้น');
        return;
      }
      
      // Validate file size (max 5MB)
      const maxSize = 5 * 1024 * 1024; // 5MB in bytes
      if (file.size > maxSize) {
        alert('ขนาดไฟล์ต้องไม่เกิน 5MB');
        return;
      }
      
      this.imageDetailFile = file;
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (e: ProgressEvent<FileReader>) => {
        this.imageDetailPreview = e.target?.result as string;
      };
      reader.readAsDataURL(file);
    }
  }

  /**
   * Remove image thumbnail
   */
  removeImageThumbnail(): void {
    this.imageThumbnailFile = null;
    this.imageThumbnailPreview = null;
    this.createCourseForm.patchValue({ imageThumbnail: null });
  }

  /**
   * Remove image detail
   */
  removeImageDetail(): void {
    this.imageDetailFile = null;
    this.imageDetailPreview = null;
    this.createCourseForm.patchValue({ imageDetail: null });
  }

  /**
   * Close modal
   */
  close(): void {
    this.activeModal.close();
  }

  /**
   * Dismiss modal
   */
  dismiss(): void {
    this.activeModal.dismiss();
  }
}
